/**
 * Pure H OS · Sprint 2 · Global Command Palette (⌘K).
 *
 * Sprint-6 integration pass: now searches BOTH the platform page/action
 * index (SEARCH_INDEX + QUICK_ACTIONS) AND the flat cross-module entity
 * index (ENTITY_INDEX in mocks/entityLinks). Results are grouped by
 * entity kind (Products / Campaigns / Batches / Suppliers / Customers /
 * Invoices / Payments / Creators / Pages / …) so a query like
 * "Nocturne Amber" surfaces the product, batches, revenue rows, and
 * invoices in one glance.
 *
 * No new visual language — composes existing Modal + TextInput +
 * ListRow + Kbd primitives.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, ArrowRight, BookOpen, Building2, Users, Bot, ClipboardCheck,
  BarChart3, FileText, Layers, User as UserIcon, Sparkles, LayoutDashboard,
  Compass, Cog, Bell, Keyboard, Package, Megaphone, Factory, CreditCard, Wallet,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { usePlatform } from "../context/PlatformContext";
import { TextInput } from "./lib";
import { Modal, ListRow, Kbd } from "./lib.overlay";
import { SEARCH_INDEX, QUICK_ACTIONS, SearchScope } from "../mocks/platformData";
import { ENTITY_INDEX, ENTITY_KIND_META, EntityKind } from "../mocks/entityLinks";

// Every source unifies into a single ResultKind used for grouping headers.
type ResultKind = EntityKind | SearchScope | "action";

const KIND_META: Record<ResultKind, { label: string; icon: LucideIcon; order: number }> = {
  // Entity kinds (module-scoped)
  product:    { label: ENTITY_KIND_META.product.label,    icon: ENTITY_KIND_META.product.icon,    order: 10 },
  campaign:   { label: ENTITY_KIND_META.campaign.label,   icon: ENTITY_KIND_META.campaign.icon,   order: 20 },
  batch:      { label: ENTITY_KIND_META.batch.label,      icon: ENTITY_KIND_META.batch.icon,      order: 30 },
  supplier:   { label: ENTITY_KIND_META.supplier.label,   icon: ENTITY_KIND_META.supplier.icon,   order: 40 },
  customer:   { label: ENTITY_KIND_META.customer.label,   icon: ENTITY_KIND_META.customer.icon,   order: 50 },
  invoice:    { label: ENTITY_KIND_META.invoice.label,    icon: ENTITY_KIND_META.invoice.icon,    order: 60 },
  payment:    { label: ENTITY_KIND_META.payment.label,    icon: ENTITY_KIND_META.payment.icon,    order: 70 },
  creator:    { label: ENTITY_KIND_META.creator.label,    icon: ENTITY_KIND_META.creator.icon,    order: 80 },
  // Platform scopes
  page:       { label: "Pages",       icon: LayoutDashboard, order: 90 },
  department: { label: "Departments", icon: Users,           order: 95 },
  report:     { label: "Reports",     icon: BarChart3,       order: 96 },
  approval:   { label: "Approvals",   icon: ClipboardCheck,  order: 97 },
  person:     { label: "People",      icon: UserIcon,        order: 98 },
  ai:         { label: "AI",          icon: Bot,             order: 99 },
  // Actions (empty query only)
  action:     { label: "Quick actions", icon: Compass,       order: 5  },
};

function scoreMatch(q: string, hay: string): number {
  const s = hay.toLowerCase();
  const query = q.toLowerCase().trim();
  if (!query) return 0;
  if (s === query) return 100;
  if (s.startsWith(query)) return 80;
  if (s.includes(query)) return 50;
  if (s.split(/\s+/).some((w) => w.startsWith(query))) return 60;
  // Multi-word support: split query on whitespace and score AND across
  // tokens — every token must appear somewhere in the haystack, and the
  // aggregate score is the sum of per-token includes-hits.
  const tokens = query.split(/\s+/).filter(Boolean);
  if (tokens.length > 1) {
    let total = 0;
    for (const t of tokens) {
      if (!s.includes(t)) return 0;
      total += s.startsWith(t) ? 30 : 15;
    }
    return total;
  }
  return 0;
}

// Parent-list pages (Reports, Meetings, ...) are down-weighted when a
// deeper page in the same tree also matches the query — otherwise the
// shorter title outscores the more specific destination and Enter lands
// on the wrong screen.
const PARENT_LIST_HREFS = new Set([
  "/pureh/reports",
  "/pureh/meetings",
  "/pureh/risks",
  "/pureh/decisions",
  "/pureh/pmo",
  "/pureh/procurement",
  "/pureh/documents",
]);

type Item = {
  kind: ResultKind;
  id: string;
  label: string;
  subtitle: string;
  icon: LucideIcon;
  href: string;
  score: number;
};

export default function CommandPalette() {
  const { paletteOpen, closePalette } = usePlatform();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (paletteOpen) { setQ(""); setCursor(0); }
  }, [paletteOpen]);

  const results = useMemo(() => {
    const items: Item[] = [];

    if (!q.trim()) {
      // Empty state → quick actions
      QUICK_ACTIONS.forEach((a) => {
        items.push({
          kind: "action",
          id: a.id,
          label: a.label,
          subtitle: a.hint,
          icon: getIconForHref(a.href),
          href: a.href ?? "",
          score: 1,
        });
      });
      return items;
    }

    // Entities across every module
    ENTITY_INDEX.forEach((r) => {
      const score = Math.max(
        scoreMatch(q, r.title),
        scoreMatch(q, r.subtitle) - 15,
        ...r.keywords.map((k) => scoreMatch(q, k) - 5),
      );
      if (score > 0) {
        items.push({
          kind: r.kind,
          id: r.id,
          label: r.title,
          subtitle: r.subtitle,
          icon: KIND_META[r.kind].icon,
          href: r.href,
          score,
        });
      }
    });

    // Platform pages / departments / people / AI / …
    SEARCH_INDEX.forEach((r) => {
      let score = Math.max(
        scoreMatch(q, r.title),
        scoreMatch(q, r.subtitle) - 20,
        ...r.keywords.map((k) => scoreMatch(q, k) - 10),
      );
      // Down-weight generic parent-list pages so a deeper match ranks first.
      if (PARENT_LIST_HREFS.has(r.href)) score -= 25;
      if (score > 0) {
        items.push({
          kind: r.scope,
          id: r.id,
          label: r.title,
          subtitle: r.subtitle,
          icon: KIND_META[r.scope].icon,
          href: r.href,
          score,
        });
      }
    });

    // Sort by score then by kind ordering for stability
    items.sort((a, b) =>
      b.score - a.score ||
      KIND_META[a.kind].order - KIND_META[b.kind].order,
    );
    return items.slice(0, 40);
  }, [q]);

  // Grouped view for rendering
  const grouped = useMemo(() => {
    const map = new Map<ResultKind, Item[]>();
    results.forEach((it) => {
      if (!map.has(it.kind)) map.set(it.kind, []);
      map.get(it.kind)!.push(it);
    });
    // Preserve KIND_META order
    return Array.from(map.entries())
      .sort((a, b) => KIND_META[a[0]].order - KIND_META[b[0]].order);
  }, [results]);

  // Flat list of items in the same visual order → drives cursor + Enter
  const flat = useMemo(() => grouped.flatMap(([, list]) => list), [grouped]);

  useEffect(() => { setCursor(0); }, [q]);

  const commit = (idx: number) => {
    const item = flat[idx];
    if (!item?.href) return;
    closePalette();
    navigate(item.href);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, flat.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === "Enter")   { e.preventDefault(); commit(cursor); }
  };

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${cursor}"]`) as HTMLElement | null;
    el?.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  return (
    <Modal open={paletteOpen} onClose={closePalette} maxWidth={640} testId="command-palette">
      <div
        className="flex items-center gap-3 px-4 py-3"
        style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}
      >
        <Search size={16} style={{ color: "var(--ph-ink-muted)" }} />
        <div className="flex-1" onKeyDown={onKey}>
          <TextInput
            value={q}
            onChange={setQ}
            placeholder="Search products · campaigns · batches · invoices · anywhere"
            testId="palette-input"
            autoFocus
          />
        </div>
        <Kbd>Esc</Kbd>
      </div>

      <div
        ref={listRef}
        data-testid="palette-results"
        className="max-h-[480px] overflow-y-auto py-1"
      >
        {flat.length === 0 && (
          <div
            className="py-10 text-center text-sm"
            style={{ color: "var(--ph-ink-muted)" }}
            data-testid="palette-empty"
          >
            No results for &quot;{q}&quot;
          </div>
        )}

        {(() => {
          let idxCounter = -1;
          return grouped.map(([kind, list]) => (
            <div key={kind} data-testid={`palette-group-${kind}`}>
              <div
                className="px-4 pt-3 pb-1 flex items-center gap-2"
                style={{
                  color: "var(--ph-ink-muted)",
                  letterSpacing: "var(--ph-tracking-eyebrow)",
                }}
              >
                <span
                  className="text-[10px] font-semibold uppercase"
                  style={{ letterSpacing: "0.14em" }}
                >
                  {KIND_META[kind].label}
                </span>
                <span
                  className="text-[10px] tabular-nums"
                  style={{ color: "var(--ph-ink-soft)" }}
                >
                  · {list.length}
                </span>
              </div>
              <div className="px-1">
                {list.map((r) => {
                  idxCounter += 1;
                  const idx = idxCounter;
                  return (
                    <div key={r.id} data-idx={idx}>
                      <ListRow
                        testId={`palette-item-${r.id}`}
                        icon={<r.icon size={15} />}
                        label={r.label}
                        sublabel={r.subtitle}
                        trailing={
                          <ArrowRight size={12} style={{ color: "var(--ph-ink-soft)" }} />
                        }
                        active={idx === cursor}
                        onClick={() => commit(idx)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ));
        })()}
      </div>

      <div
        className="flex items-center justify-between px-4 py-2"
        style={{
          borderTop: "1px solid var(--ph-border-subtle)",
          background: "var(--ph-surface)",
          borderBottomLeftRadius: "var(--ph-radius-lg)",
          borderBottomRightRadius: "var(--ph-radius-lg)",
        }}
      >
        <div className="flex items-center gap-3 text-[11px]" style={{ color: "var(--ph-ink-muted)" }}>
          <span className="inline-flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd> Navigate</span>
          <span className="inline-flex items-center gap-1"><Kbd>↵</Kbd> Open</span>
          <span className="inline-flex items-center gap-1"><Kbd>Esc</Kbd> Close</span>
        </div>
        <div className="text-[11px]" style={{ color: "var(--ph-ink-muted)" }}>
          <span className="inline-flex items-center gap-1"><BookOpen size={11} /> Pure H OS</span>
        </div>
      </div>
    </Modal>
  );
}

function getIconForHref(href?: string): LucideIcon {
  if (!href) return Compass;
  if (href.includes("/dashboard"))     return LayoutDashboard;
  if (href.includes("/finance"))       return Wallet;
  if (href.includes("/notifications")) return Bell;
  if (href.includes("/approvals"))     return ClipboardCheck;
  if (href.includes("/reports"))       return BarChart3;
  if (href.includes("/ai"))            return Sparkles;
  if (href.includes("/departments"))   return Users;
  if (href.includes("/company"))       return Building2;
  if (href.includes("/workspace"))     return Layers;
  if (href.includes("/profile"))       return UserIcon;
  if (href.includes("/settings"))      return Cog;
  if (href.includes("/shortcuts"))     return Keyboard;
  if (href.includes("/search"))        return Search;
  if (href.includes("/marketing"))     return Megaphone;
  if (href.includes("/commerce"))      return Package;
  if (href.includes("/operations"))    return Factory;
  if (href.includes("/vendors"))       return CreditCard;
  return Compass;
}
